import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyWithdraw,
  getWithdrawDetail,
  listBill,
  listWithdraws,
  rechargeBalance,
} from "../endpoints/wallet";
import { useBackendCurrency, useBackendLang } from "../lang-context";
import type { BillPage, WithdrawApply, WithdrawApplyInput, WithdrawPage } from "../types/wallet";

const WITHDRAW_LIST_KEY = ["wallet", "withdraw-list"] as const;
const BILL_KEY = ["wallet", "bill"] as const;

export function useWithdrawList(
  authToken: string | null,
  input: { pageNo: number; pageSize: number; type?: string } = {
    pageNo: 1,
    pageSize: 10,
  },
) {
  return useQuery<WithdrawPage>({
    queryKey: [...WITHDRAW_LIST_KEY, input.pageNo, input.pageSize, input.type],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return listWithdraws({ baseUrl: API_BASE_URL }, authToken, input);
    },
    staleTime: 30_000,
  });
}

export function useWithdrawDetail(authToken: string | null, withdrawApplyId: string | null) {
  return useQuery<WithdrawApply>({
    queryKey: ["wallet", "withdraw-detail", withdrawApplyId],
    enabled: !!authToken && !!withdrawApplyId,
    queryFn: () => {
      if (!authToken || !withdrawApplyId) throw new Error("NO_TOKEN");
      return getWithdrawDetail({ baseUrl: API_BASE_URL }, authToken, withdrawApplyId);
    },
    staleTime: 60_000,
  });
}

export function useApplyWithdraw(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WithdrawApplyInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return applyWithdraw({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WITHDRAW_LIST_KEY });
    },
  });
}

/**
 * Create a wallet top-up order. Resolves to the recharge `orderNo`, which the
 * caller hands to the /pay cashier (it is a normal PayOrder of type RECHARGE).
 */
export function useRechargeBalance(authToken: string | null) {
  return useMutation({
    mutationFn: async (input: { rechargeAmount: number; currency?: string }) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return rechargeBalance({ baseUrl: API_BASE_URL }, authToken, input);
    },
  });
}

export function useBillList(
  authToken: string | null,
  input: {
    pageNo: number;
    pageSize: number;
    changeType?: string;
    orderNo?: string;
    payOrderNo?: string;
  } = { pageNo: 1, pageSize: 10 },
) {
  // Thread the active backend lang so `type`/`remark` are localized to the UI
  // locale, and key on it so a locale switch refetches instead of serving the
  // stale-cached language.
  const lang = useBackendLang();
  const currency = useBackendCurrency();
  return useQuery<BillPage>({
    queryKey: [
      ...BILL_KEY,
      input.pageNo,
      input.pageSize,
      input.changeType,
      input.orderNo,
      input.payOrderNo,
      lang,
      currency,
    ],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return listBill({ baseUrl: API_BASE_URL }, authToken, input, { lang, currency });
    },
    staleTime: 30_000,
  });
}
